import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertCircle, Download, FolderOpen, RefreshCw, Save, Brain, Zap, ChevronDown, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document type categories for organized display
const DOC_CATEGORIES = [
  { name: 'TITLE', label: 'Title Documents', icon: '📜' },
  { name: 'ENCUMBRANCE', label: 'Encumbrance', icon: '🔍' },
  { name: 'REVENUE', label: 'Revenue Records', icon: '📋' },
  { name: 'PERMISSIONS', label: 'Permissions', icon: '🏗️' },
  { name: 'TAX', label: 'Tax Records', icon: '💰' },
  { name: 'IDENTITY', label: 'Identity', icon: '🪪' },
  { name: 'SUCCESSION', label: 'Succession', icon: '👨‍👩‍👧‍👦' },
  { name: 'POWER', label: 'Powers of Attorney', icon: '📝' },
  { name: 'AGREEMENT', label: 'Agreements', icon: '🤝' },
  { name: 'NOC', label: 'NOCs', icon: '✅' },
  { name: 'CERTIFICATE', label: 'Certificates', icon: '📃' },
  { name: 'OTHER', label: 'Other', icon: '📄' }
];

const PROPERTY_TYPES = [
  { value: 'AGRICULTURAL', label: 'Agricultural Land' },
  { value: 'PLOT', label: 'Plot / Venture' },
  { value: 'FLAT', label: 'Flat / Apartment' },
  { value: 'HOUSE', label: 'House' },
  { value: 'VILLA', label: 'Villa' },
  { value: 'COMMERCIAL_BUILDING', label: 'Commercial Building' }
];

const PROFILE_TYPES = [
  { value: 'RETAIL', label: 'Retail', description: 'Standard check' },
  { value: 'APF', label: 'APF', description: 'Approved Project Finance' },
  { value: 'SCRUTINY', label: 'Scrutiny', description: 'Detailed verification' }
];

const PriorityBadge = ({ priority }) => {
  const colors = {
    MANDATORY: 'bg-red-100 text-red-800 border-red-200',
    CONDITIONAL: 'bg-orange-100 text-orange-800 border-orange-200',
    OPTIONAL: 'bg-green-100 text-green-800 border-green-200'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${colors[priority] || colors.OPTIONAL}`}>
      {priority}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    INCOMPLETE: 'bg-yellow-100 text-yellow-800',
    READY_FOR_OPINION: 'bg-green-100 text-green-800',
    OPENED_BY_ADVOCATE: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-gray-100 text-gray-800'
  };
  
  const labels = {
    INCOMPLETE: 'Incomplete',
    READY_FOR_OPINION: 'Ready for Opinion',
    OPENED_BY_ADVOCATE: 'In Progress',
    COMPLETED: 'Completed'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status]}`}>
      {labels[status] || status}
    </span>
  );
};

const ConfidenceBadge = ({ confidence }) => {
  const colors = {
    HIGH: 'bg-green-100 text-green-700',
    MEDIUM: 'bg-yellow-100 text-yellow-700',
    LOW: 'bg-gray-100 text-gray-600'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${colors[confidence]}`}>
      {confidence === 'HIGH' ? '✓ Auto' : confidence === 'MEDIUM' ? '? Maybe' : '? Unknown'}
    </span>
  );
};

// NEW: Risk Level Badge for Act Engine results
const RiskBadge = ({ risk }) => {
  const config = {
    CRITICAL: { color: 'bg-red-600 text-white', icon: '🔴', label: 'Critical' },
    HIGH: { color: 'bg-orange-500 text-white', icon: '🟠', label: 'High' },
    MEDIUM: { color: 'bg-yellow-400 text-yellow-900', icon: '🟡', label: 'Medium' },
    LOW: { color: 'bg-green-100 text-green-700', icon: '🟢', label: 'Low' },
    NONE: { color: 'bg-gray-100 text-gray-600', icon: '⚪', label: 'None' }
  };
  
  const cfg = config[risk] || config.MEDIUM;
  
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// NEW: Source Badge to show where the missing doc requirement came from
const SourceBadge = ({ source }) => {
  const config = {
    // PRIMARY (Authoritative) - From deed recitals
    RECITAL_ANALYSIS: { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: '📜', label: 'From Recitals' },
    // Legal requirements
    ACT_ENGINE: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '⚖️', label: 'Legal Act' },
    // Legacy / Advisory sources (informational only)
    RULE_ENGINE: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: '📋', label: 'Advisory' },
    ADVISORY_CHECKLIST: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: '📋', label: 'Advisory' },
    ADVISORY_TITLE_CONTINUITY: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: '🔗', label: 'Chain Advisory' },
    INTELLIGENT_ANALYSIS: { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: '🧠', label: 'AI Reference' },
    TITLE_CONTINUITY_ENGINE: { color: 'bg-cyan-100 text-cyan-700 border-cyan-200', icon: '📜', label: 'Chain Advisory' }
  };
  
  const cfg = config[source] || config.ADVISORY_CHECKLIST;
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// Title Continuity Status Badge - with confidence-aware color
// Rule: Never show green if confidence < 60%
const ChainStatusBadge = ({ status, confidence }) => {
  const config = {
    CONTINUOUS: { color: 'bg-green-500 text-white', icon: '🟢', label: 'Continuous' },
    BROKEN: { color: 'bg-red-500 text-white', icon: '🔴', label: 'Broken' },
    INCOMPLETE: { color: 'bg-yellow-500 text-white', icon: '🟡', label: 'Incomplete' }
  };
  
  let cfg = config[status] || config.INCOMPLETE;
  
  // IMPORTANT: Never show green if confidence < 60%
  // This prevents misleading "Continuous" with low confidence
  if (status === 'CONTINUOUS' && confidence < 0.60) {
    cfg = config.INCOMPLETE;
  }
  
  return (
    <span className={`px-3 py-1 text-sm font-semibold rounded ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// Link Status Badge for timeline nodes
const LinkStatusBadge = ({ status, score }) => {
  const config = {
    VALID: { color: 'bg-green-500', border: 'border-green-500', dot: '🟢' },
    UNCERTAIN: { color: 'bg-yellow-500', border: 'border-yellow-500', dot: '🟡' },
    NAME_MISMATCH: { color: 'bg-red-500', border: 'border-red-500', dot: '🔴' },
    START: { color: 'bg-blue-500', border: 'border-blue-500', dot: '🔵' },
    INCOMPLETE: { color: 'bg-gray-400', border: 'border-gray-400', dot: '⚪' },
    PENDING: { color: 'bg-gray-300', border: 'border-gray-300', dot: '⚪' },
    PARALLEL: { color: 'bg-purple-500', border: 'border-purple-500', dot: '🟣' },
    BREAK: { color: 'bg-red-600', border: 'border-red-600', dot: '🔴' },
    EXPLAINABLE: { color: 'bg-yellow-400', border: 'border-yellow-400', dot: '🟡' },
    ENCUMBRANCE: { color: 'bg-blue-400', border: 'border-blue-400', dot: '🔷' },
    ROOT_OF_TITLE: { color: 'bg-green-600', border: 'border-green-600', dot: '🟢' },
    // New multi-trunk statuses (v5.0)
    SUCCESSION_GAP: { color: 'bg-orange-500', border: 'border-orange-500', dot: '🟠' },
    RECITAL_VALIDATED: { color: 'bg-emerald-500', border: 'border-emerald-500', dot: '📜' },
    PARALLEL_CHAIN: { color: 'bg-purple-600', border: 'border-purple-600', dot: '🌳' }
  };
  
  const cfg = config[status] || config.PENDING;
  
  return (
    <div className={`w-4 h-4 rounded-full ${cfg.color}`} title={`${status}${score ? ` (${Math.round(score * 100)}%)` : ''}`} />
  );
};

// NEW: Observation Type Badge for Dual-Tree Engine
const ObservationBadge = ({ type, riskLevel }) => {
  const config = {
    HEALTHY: { color: 'bg-green-100 text-green-700 border-green-200', icon: '✅' },
    RISK: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: '⚠️' },
    CRITICAL: { color: 'bg-red-100 text-red-700 border-red-200', icon: '🔴' },
    MISMATCH: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: '⚡' },
    INFO: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'ℹ️' }
  };
  
  const cfg = config[type] || config.INFO;
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${cfg.color}`}>
      {cfg.icon} {type}
    </span>
  );
};

// NEW: Tree Source Badge (Deed vs Revenue)
const TreeSourceBadge = ({ source }) => {
  const config = {
    DEED: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: '📜', label: 'Deed' },
    REVENUE: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '📋', label: 'Revenue' }
  };
  
  const cfg = config[source] || config.DEED;
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

export default function MissingDocsChecker() {
  const navigate = useNavigate();
  
  // State
  const [step, setStep] = useState(1); // 1: Upload, 2: Configure, 3: Results
  const [propertyType, setPropertyType] = useState('AGRICULTURAL');
  const [profile, setProfile] = useState('RETAIL');
  const [docTypes, setDocTypes] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [caseName, setCaseName] = useState('');
  const [flags, setFlags] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [useIntelligentAnalysis, setUseIntelligentAnalysis] = useState(false);
  const [chainAnalysis, setChainAnalysis] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [useAIClassification, setUseAIClassification] = useState(true);
  // Act Engine state
  const [triggeredActs, setTriggeredActs] = useState([]);
  const [riskScore, setRiskScore] = useState('NONE');
  // Title Continuity state (NEW)
  const [titleContinuity, setTitleContinuity] = useState(null);
  
  // EC Comparison state (NEW - Documentation Risk vs Ownership Risk)
  const [ecComparison, setEcComparison] = useState(null);
  const [ecComparisonLoading, setEcComparisonLoading] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState('title'); // 'title' or 'ec'
  
  // Document status for Original/Copy tracking
  const [docStatuses, setDocStatuses] = useState({}); // {fileIndex: 'Original' | 'Xerox' | 'Certified Copy'}
  
  // Auto-run EC Comparison when analysis results are available
  useEffect(() => {
    if (step === 3 && result && !ecComparison && !ecComparisonLoading) {
      // Auto-trigger EC comparison after analysis completes
      runEcComparisonAuto();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, result]);
  
  // Fetch document types on mount
  useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/missing-docs/doc-types`);
        setDocTypes(data);
      } catch (err) {
        console.error('Failed to fetch doc types:', err);
        setError('Failed to load document types. Please refresh the page.');
      }
    };
    fetchDocTypes();
  }, []);
  
  // Group doc types by category
  const docTypesByCategory = docTypes.reduce((acc, dt) => {
    if (!acc[dt.category]) acc[dt.category] = [];
    acc[dt.category].push(dt);
    return acc;
  }, {});
  
  // Progress state for batch classification
  const [classifyProgress, setClassifyProgress] = useState({ current: 0, total: 0, status: '' });
  
  // AI Classification function - reads document content to classify (with batching for large sets)
  const classifyWithAI = useCallback(async () => {
    if (uploadedFiles.length === 0) return;
    
    setClassifying(true);
    setError(null);
    
    // Process ONE FILE AT A TIME to avoid timeouts (AI classification can take 30-90 seconds per file)
    const filesToClassify = uploadedFiles.filter(f => f.file);
    const totalFiles = filesToClassify.length;
    
    console.log(`🤖 Starting AI classification for ${totalFiles} files ONE AT A TIME...`);
    setClassifyProgress({ current: 0, total: totalFiles, status: 'Starting...' });
    
    try {
      // Process ONE FILE AT A TIME and update immediately
      for (let i = 0; i < filesToClassify.length; i++) {
        const currentFile = filesToClassify[i];
        const currentFileName = currentFile.file_name; // Capture filename for logging
        const currentFileRef = currentFile.file; // Use File object reference for matching
        
        setClassifyProgress({ 
          current: i, 
          total: totalFiles, 
          status: `Classifying file ${i + 1} of ${totalFiles}...`,
          fileName: currentFileName
        });
        
        console.log(`🤖 [${i + 1}/${totalFiles}] Classifying: ${currentFileName}`);
        
        const formData = new FormData();
        formData.append('files', currentFileRef);
        formData.append('use_ai', 'true');
        
        try {
          const { data } = await axios.post(`${API_URL}/api/missing-docs/classify-documents`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000 // 3 minute timeout per file (AI can be slow)
          });
          
          if (data.classified_documents && data.classified_documents.length > 0) {
            const result = data.classified_documents[0];
            console.log(`✅ [${i + 1}/${totalFiles}] ${currentFileName} -> ${result.detected_type} (${result.classified_by})`);
            
            // IMMEDIATELY update this specific file in state
            // Match by File object REFERENCE to avoid any string comparison issues
            setUploadedFiles(prev => prev.map((f) => {
              // Use File object reference for exact matching (no string comparison issues)
              if (f.file === currentFileRef) {
                console.log(`   ✅ Updating state for: ${f.file_name} via file reference match`);
                return {
                  ...f,
                  doc_type: result.detected_type,
                  doc_type_label: result.detected_label,
                  confidence: result.confidence,
                  classified_by: result.classified_by,
                  auto_detected: true
                };
              }
              return f;
            }));
          } else {
            console.log(`⚠️ [${i + 1}/${totalFiles}] ${currentFileName} -> No result returned`);
            setUploadedFiles(prev => prev.map((f) => {
              if (f.file === currentFileRef) {
                return { ...f, classified_by: 'FAILED', confidence: 'LOW' };
              }
              return f;
            }));
          }
        } catch (fileErr) {
          console.error(`❌ [${i + 1}/${totalFiles}] ${currentFileName} failed:`, fileErr.message);
          setUploadedFiles(prev => prev.map((f) => {
            if (f.file === currentFileRef) {
              return { ...f, classified_by: 'FAILED', confidence: 'LOW' };
            }
            return f;
          }));
        }
      }
      
      setClassifyProgress({ current: totalFiles, total: totalFiles, status: 'Complete!' });
      console.log('✅ All files classified');
      
    } catch (err) {
      console.error('AI Classification failed:', err);
      setError('AI classification failed. You can still manually select document types.');
    } finally {
      setClassifying(false);
      setTimeout(() => setClassifyProgress({ current: 0, total: 0, status: '', fileName: '' }), 3000);
    }
  }, [uploadedFiles]);
  
  // Handle file drop/select
  const handleFiles = useCallback(async (files) => {
    const fileList = Array.from(files);
    const fileNames = fileList.map(f => f.name);
    setError(null);
    
    try {
      // Auto-detect document types from filenames first (fast)
      const { data: detected } = await axios.post(`${API_URL}/api/missing-docs/detect`, fileNames);
      
      const newFiles = fileList.map((file, idx) => ({
        file,
        file_name: file.name,
        doc_type: detected[idx]?.detected_type || 'OTHER',
        doc_type_label: detected[idx]?.detected_label || 'Other Document',
        confidence: detected[idx]?.confidence || 'LOW',
        classified_by: 'FILENAME',
        auto_detected: true
      }));
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
    } catch (err) {
      console.error('Detection failed:', err);
      // Add files without detection
      const newFiles = fileList.map(file => ({
        file,
        file_name: file.name,
        doc_type: 'OTHER',
        doc_type_label: 'Other Document',
        confidence: 'LOW',
        classified_by: 'NONE',
        auto_detected: false
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  }, []);
  
  // Drag handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };
  
  // Update document type
  const updateDocType = (index, newType) => {
    const docInfo = docTypes.find(d => d.value === newType);
    setUploadedFiles(prev => prev.map((f, i) => 
      i === index 
        ? { ...f, doc_type: newType, doc_type_label: docInfo?.label || newType, auto_detected: false }
        : f
    ));
  };
  
  // Remove file
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Progress state for upload
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: '', fileName: '' });
  
  // Run check (without file upload - just rule engine)
  const runCheck = async () => {
    setLoading(true);
    setError(null);
    setChainAnalysis(null);
    
    try {
      if (useIntelligentAnalysis) {
        // INTELLIGENT ANALYSIS: Only extract TITLE DEEDS (they have references to other docs)
        // Other docs (PPB, EC, tax receipts) don't need extraction - just rule check
        const TITLE_DEEDS = new Set([
          'SALE_DEED', 'GIFT_DEED', 'PARTITION_DEED', 'RELEASE_DEED', 'SETTLEMENT_DEED',
          'MORTGAGE_DEED', 'GPA', 'SPA', 'AGREEMENT_OF_SALE', 'WILL', 'DEVELOPMENT_AGREEMENT'
        ]);
        
        const titleDeeds = uploadedFiles.filter(f => 
          f.file && TITLE_DEEDS.has(f.doc_type?.toUpperCase())
        );
        const otherDocs = uploadedFiles.filter(f => 
          !TITLE_DEEDS.has(f.doc_type?.toUpperCase())
        );
        
        console.log(`🧠 Intelligent analysis: ${titleDeeds.length} title deeds to extract, ${otherDocs.length} other docs for rule check`);
        
        if (titleDeeds.length === 0) {
          console.log('⚠️ No title deeds found - running rule-based check only');
        }
        
        // ============================================================
        // ONE FILE AT A TIME UPLOAD with Progress Bar
        // ============================================================
        const totalFiles = titleDeeds.length;
        
        if (totalFiles > 0) {
          console.log(`📤 Processing ${totalFiles} files ONE AT A TIME to avoid 413 errors...`);
          
          // Generate a unique session ID for this batch of uploads
          const batchSessionId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          console.log(`📌 Batch session ID: ${batchSessionId}`);
          
          for (let fileIdx = 0; fileIdx < totalFiles; fileIdx++) {
            const currentFile = titleDeeds[fileIdx];
            const isLastFile = fileIdx === totalFiles - 1;
            
            // Update progress bar
            setUploadProgress({ 
              current: fileIdx, 
              total: totalFiles, 
              status: isLastFile 
                ? `Analyzing all ${totalFiles} documents...` 
                : `Processing file ${fileIdx + 1} of ${totalFiles}...`,
              fileName: currentFile.file_name
            });
            
            console.log(`📤 [${fileIdx + 1}/${totalFiles}] Uploading: ${currentFile.file_name}`);
            
            const singleFormData = new FormData();
            singleFormData.append('files', currentFile.file);
            singleFormData.append('doc_types', currentFile.doc_type);
            
            // Only send full metadata on the LAST file to trigger complete analysis
            if (isLastFile) {
              singleFormData.append('all_docs_metadata', JSON.stringify(uploadedFiles.map(f => ({
                file_name: f.file_name,
                doc_type: f.doc_type,
                doc_type_label: f.doc_type_label
              }))));
            } else {
              // For intermediate files, send empty metadata (extraction only)
              singleFormData.append('all_docs_metadata', JSON.stringify([]));
            }
            
            singleFormData.append('profile', profile);
            singleFormData.append('property_type', propertyType);
            singleFormData.append('flags', JSON.stringify(flags));
            singleFormData.append('batch_mode', 'true');
            singleFormData.append('batch_index', String(fileIdx));
            singleFormData.append('total_batches', String(totalFiles));
            singleFormData.append('batch_session_id', batchSessionId);
            
            try {
              const { data: fileData } = await axios.post(`${API_URL}/api/missing-docs/analyze-direct`, singleFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000, // 2 minute timeout per file
              });
              
              console.log(`✅ [${fileIdx + 1}/${totalFiles}] Complete: ${currentFile.file_name}`, 
                fileData.is_final ? '(FINAL - Full analysis)' : '(Extraction only)');
              
              // Only process final results on the last file
              // Check for new 3-table model response OR legacy combined_missing_docs
              if (isLastFile && (fileData.referenced_documents || fileData.missing_documents || fileData.combined_missing_docs)) {
                // Store chain analysis
                setChainAnalysis(fileData.chain_analysis);
                
                // Capture Act Engine results
                setTriggeredActs(fileData.triggered_acts || []);
                setRiskScore(fileData.risk_score || 'NONE');
                
                // Capture Title Continuity result
                setTitleContinuity(fileData.title_continuity || null);
                
                // ============================================================
                // 3-TABLE MODEL: Document Accounting (NO inference)
                // ============================================================
                // TABLE 1: Documents Referenced in Title
                // TABLE 2: Missing = Referenced - Uploaded
                // TABLE 3: Legal Acts Triggered
                // ============================================================
                
                const referencedDocuments = fileData.referenced_documents || [];
                const missingDocuments = fileData.missing_documents || [];
                const matchedDocuments = fileData.matched_documents || [];
                const possiblySame = fileData.possibly_same || [];
                const table1Result = fileData.table1_referenced || {};
                const table2Result = fileData.table2_comparison || {};
                
                // Debug log
                console.log('📋 3-TABLE MODEL:', {
                  'TABLE 1 (Referenced)': referencedDocuments.length,
                  'TABLE 2 (Missing)': missingDocuments.length,
                  'TABLE 2 (Matched)': matchedDocuments.length,
                  'TABLE 2 (Possibly Same)': possiblySame.length,
                  'TABLE 3 (Acts)': fileData.triggered_acts?.length || 0
                });
                
                setResult({
                  profile,
                  property_type: propertyType,
                  available_docs: fileData.available_docs || uploadedFiles.map(f => ({
                    file_name: f.file_name,
                    doc_type: f.doc_type,
                    doc_type_label: f.doc_type_label
                  })),
                  
                  // TABLE 1: Documents Referenced in Title
                  referenced_documents: referencedDocuments,
                  table1_stats: table1Result.stats || {},
                  table1_status: table1Result.status || 'UNKNOWN',
                  
                  // TABLE 2: Missing from Uploaded Set
                  missing_documents: missingDocuments,
                  matched_documents: matchedDocuments,
                  possibly_same: possiblySame,
                  table2_stats: table2Result.stats || {},
                  table2_status: table2Result.status || 'UNKNOWN',
                  
                  // TABLE 3: Legal Acts Triggered
                  triggered_acts: fileData.triggered_acts || [],
                  act_based_missing: fileData.act_based_missing || [],
                  
                  // Supporting data
                  title_continuity: fileData.title_continuity || null,
                  risk_score: fileData.risk_score || 'NONE',
                  successfully_extracted: fileData.successfully_extracted || 0,
                  system_description: fileData.system_description || '',
                  
                  // Computed values for UI
                  is_ready_for_opinion: missingDocuments.length === 0 && uploadedFiles.length > 0,
                  intelligent_analysis: true
                });
              }
            } catch (fileErr) {
              console.error(`❌ [${fileIdx + 1}/${totalFiles}] Failed: ${currentFile.file_name}`, fileErr);
              // Continue with next file instead of failing completely
              if (isLastFile) {
                throw new Error(`Failed to process ${currentFile.file_name}: ${fileErr.message}`);
              }
            }
          }
          
          // Update progress to complete
          setUploadProgress({ current: totalFiles, total: totalFiles, status: 'Analysis complete!', fileName: '' });
          
          setStep(3);
          setLoading(false);
          setTimeout(() => setUploadProgress({ current: 0, total: 0, status: '', fileName: '' }), 2000);
          return;
        }
        
        // ============================================================
        // No title deeds - run rule-based check only (original code)
        // ============================================================
        console.log(`📤 No title deeds to extract, running rule-based check only...`);
        
        const formData = new FormData();
        formData.append('all_docs_metadata', JSON.stringify(uploadedFiles.map(f => ({
          file_name: f.file_name,
          doc_type: f.doc_type,
          doc_type_label: f.doc_type_label
        }))));
        formData.append('profile', profile);
        formData.append('property_type', propertyType);
        formData.append('flags', JSON.stringify(flags));
        
        // Call rule-based check endpoint instead
        const { data } = await axios.post(`${API_URL}/api/missing-docs/check`, {
          profile,
          property_type: propertyType,
          uploaded_docs: uploadedFiles.map(f => ({
            file_name: f.file_name,
            doc_type: f.doc_type,
            doc_type_label: f.doc_type_label,
            auto_detected: f.auto_detected
          })),
          flags
        });
        
        setResult(data);
        setTriggeredActs([]);
        setRiskScore('NONE');
        setUploadProgress({ current: 0, total: 0, status: '', fileName: '' });
      } else {
        // Standard rule-based check (no file upload needed)
        const { data } = await axios.post(`${API_URL}/api/missing-docs/check`, {
          profile,
          property_type: propertyType,
          uploaded_docs: uploadedFiles.map(f => ({
            file_name: f.file_name,
            doc_type: f.doc_type,
            doc_type_label: f.doc_type_label,
            auto_detected: f.auto_detected
          })),
          flags
        });
        setResult(data);
        setTriggeredActs([]);
        setRiskScore('NONE');
      }
      setStep(3);
    } catch (err) {
      setError('Error running check: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Save case to database
  const saveCase = async () => {
    if (!result) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/missing-docs/save-case`, {
        case_name: caseName || undefined,
        profile,
        property_type: propertyType,
        uploaded_docs: uploadedFiles.map(f => ({
          file_name: f.file_name,
          doc_type: f.doc_type,
          doc_type_label: f.doc_type_label,
          auto_detected: f.auto_detected
        })),
        missing_docs: result.missing_documents || result.missing_docs,
        completeness_percent: result.completeness_percent,
        flags
      });
      alert(`Case saved successfully!\n\nCase ID: ${data.case_id}\nStatus: ${data.status}`);
    } catch (err) {
      setError('Error saving case: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };
  
  // ============================================================
  // EC-DOCUMENT COMPARISON (Documentation Risk Analysis)
  // Auto-runs as part of Missing Document Identifier generation
  // ============================================================
  const runEcComparisonAuto = async () => {
    if (!result) return;
    
    setEcComparisonLoading(true);
    
    try {
      let ecEntries = [];
      let ecSource = 'NONE';
      
      // Check if user uploaded an actual EC document
      const ecDocuments = uploadedFiles.filter(f => 
        f.doc_type === 'EC' || 
        f.doc_type_label?.toLowerCase().includes('encumbrance') ||
        f.file_name?.toLowerCase().includes('ec')
      );
      
      if (ecDocuments.length > 0) {
        console.log('📋 Found EC document(s):', ecDocuments.map(f => ({ 
          file_name: f.file_name, 
          doc_type: f.doc_type,
          has_file: !!f.file 
        })));
        ecSource = 'EC_DOCUMENT';
        
        // Try to parse EC document(s) for transaction entries
        for (const ecDoc of ecDocuments) {
          try {
            console.log(`🔍 Processing EC doc: ${ecDoc.file_name}, has file object: ${!!ecDoc.file}`);
            
            // If the EC has a file object, upload and parse it
            if (ecDoc.file) {
              const formData = new FormData();
              formData.append('file', ecDoc.file);
              
              console.log(`📤 Sending EC to parse endpoint: ${ecDoc.file_name}`);
              
              const parseResponse = await axios.post(
                `${API_URL}/api/missing-docs/parse-ec-document`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
              
              console.log(`📥 Parse response:`, parseResponse.data);
              
              if (parseResponse.data.success && parseResponse.data.ec_entries?.length > 0) {
                ecEntries.push(...parseResponse.data.ec_entries);
                console.log(`✅ Parsed ${parseResponse.data.ec_entries.length} entries from ${ecDoc.file_name}`);
              } else if (parseResponse.data.error) {
                console.warn(`⚠️ EC parsing error: ${parseResponse.data.error}`);
              }
            } else {
              console.warn(`⚠️ EC document ${ecDoc.file_name} has no file object - cannot parse`);
            }
          } catch (parseErr) {
            console.error(`❌ Could not parse EC document ${ecDoc.file_name}:`, parseErr);
          }
        }
        
        // If EC documents were found but no entries parsed, show warning
        if (ecDocuments.length > 0 && ecEntries.length === 0) {
          console.warn('⚠️ EC documents found but no entries could be parsed');
        }
      }
      
      // If no EC document uploaded, set result with warning message
      if (ecDocuments.length === 0) {
        console.log('⚠️ No EC document uploaded - EC Comparison skipped');
        setEcComparison({
          success: true,
          no_ec_uploaded: true,
          matched_documents: [],
          discrepancy_documents: [],
          missing_documents: {
            from_ec: [],
            from_references: [],
            from_checklist: []
          },
          statistics: {
            total_ec_entries: 0,
            total_uploaded: uploadedFiles.length,
            total_referenced: (result.referenced_documents || []).length,
            total_matched: 0,
            total_missing: 0,
            completion_percentage: 0
          }
        });
        setEcComparisonLoading(false);
        return;
      }
      
      console.log('🔍 EC Entries summary:', {
        total: ecEntries.length,
        source: ecSource
      });
      
      // Prepare uploaded documents with all available extracted data
      const uploadedDocs = (result.available_docs || uploadedFiles).map(f => {
        // Get extracted data from multiple possible sources
        const extracted = f.extracted_data || f;
        
        // Debug: Log each file's extracted data
        console.log(`📄 File: ${f.file_name}`, {
          has_extracted_data: !!f.extracted_data,
          extracted_doc_no: extracted.doc_no,
          extracted_year: extracted.year,
          raw_extracted: f.extracted_data
        });
        
        return {
          file_name: f.file_name,
          doc_type: f.doc_type,
          doc_type_label: f.doc_type_label,
          extracted_data: {
            doc_no: extracted.doc_no || extracted.document_number || extracted.sro_doc_no || f.doc_no,
            date: extracted.date || extracted.execution_date || extracted.registration_date || f.date || f.year,
            year: extracted.year || f.year,
            sro: extracted.sro || extracted.sro_name || extracted.sub_registrar_office || f.sro,
            seller: extracted.seller || extracted.executant || extracted.from_owner || f.from_owner,
            buyer: extracted.buyer || extracted.claimant || extracted.to_owner || f.to_owner,
            survey_no: extracted.survey_no || extracted.sy_no || f.survey_no,
            extent: extracted.extent || extracted.area || f.extent
          }
        };
      });
      
      console.log('📋 Uploaded docs for comparison:', uploadedDocs.map(d => ({
        file: d.file_name,
        type: d.doc_type,
        doc_no: d.extracted_data.doc_no,
        year: d.extracted_data.year
      })));
      
      // Prepare referenced documents from result
      const referencedDocs = (result.referenced_documents || []).map(ref => ({
        doc_type: ref.doc_type,
        doc_no: ref.doc_no,
        date: ref.date,
        sro: ref.sro,
        parties: ref.parties,
        source_deed: ref.source_deed,
        referenced_in: ref.source_deed
      }));
      
      console.log('🔍 Running EC Comparison:', {
        ec_entries: ecEntries.length,
        uploaded_docs: uploadedDocs.length,
        referenced_docs: referencedDocs.length
      });
      
      const { data } = await axios.post(`${API_URL}/api/missing-docs/ec-comparison`, {
        ec_entries: ecEntries,
        uploaded_docs: uploadedDocs,
        referenced_docs: referencedDocs,
        profile: profile,
        property_type: propertyType
      });
      
      if (data.success) {
        setEcComparison({ ...data, no_ec_uploaded: false });
        console.log('✅ EC Comparison complete:', data.statistics);
      } else {
        console.warn('⚠️ EC Comparison returned no results');
      }
    } catch (err) {
      console.error('❌ EC Comparison failed:', err);
      // Don't show error to user for auto-run - just log it
    } finally {
      setEcComparisonLoading(false);
    }
  };
  
  // Download checklist as TXT, PDF, or Word
  const downloadChecklist = async (format = 'txt') => {
    if (!result) return;
    
    // PDF and DOCX use API endpoint
    if (format === 'pdf' || format === 'docx') {
      try {
        // Debug log ec_comparison data
        console.log('📥 Sending ec_comparison to generate-checklist:', ecComparison);
        console.log('   EC docs count:', ecComparison?.ec_docs?.length || 0);
        console.log('   Matched count:', ecComparison?.ec_vs_uploaded?.summary?.matched_count || 0);
        
        const response = await axios.post(
          `${API_URL}/api/missing-docs/generate-checklist`,
          {
            profile,
            property_type: propertyType,
            completeness_percent: result.completeness_percent || 0,
            risk_score: riskScore,
            available_docs: result.available_docs || [],
            missing_docs: result.missing_documents || result.missing_docs || [],
            referenced_documents: result.referenced_documents || [],
            matched_documents: result.matched_documents || [],
            triggered_acts: triggeredActs || [],
            title_continuity: titleContinuity,
            ec_comparison: ecComparison,
            is_ready_for_opinion: result.is_ready_for_opinion || false,
            format: format
          },
          { responseType: 'blob' }
        );
        
        // Set mime type and extension based on format
        const mimeTypes = {
          pdf: 'application/pdf',
          docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        };
        const extensions = { pdf: 'pdf', docx: 'docx' };
        
        const url = URL.createObjectURL(new Blob([response.data], { type: mimeTypes[format] }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `missing_docs_checklist_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error(`Error generating ${format.toUpperCase()}:`, err);
        setError(`Failed to generate ${format.toUpperCase()}. Please try TXT format.`);
      }
      return;
    }
    
    // Generate TXT content (existing logic)
    let content = `MISSING DOCUMENTS CHECKLIST\n`;
    content += `${'='.repeat(50)}\n\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `Profile: ${profile}\n`;
    content += `Property Type: ${PROPERTY_TYPES.find(p => p.value === propertyType)?.label}\n`;
    content += `Completeness: ${result.completeness_percent}%\n`;
    content += `Risk Score: ${riskScore}\n\n`;
    
    // Triggered Acts Section
    if (triggeredActs.length > 0) {
      content += `LEGAL ACTS TRIGGERED (${triggeredActs.length})\n`;
      content += `${'-'.repeat(50)}\n`;
      triggeredActs.forEach((act, i) => {
        content += `${i + 1}. ${act.act_name} [${act.category}]\n`;
        content += `   ${act.obligation}\n\n`;
      });
    }
    
    // Title Continuity Section
    if (titleContinuity && titleContinuity.chain_length > 0) {
      content += `\nTITLE CONTINUITY ANALYSIS\n`;
      content += `${'-'.repeat(50)}\n`;
      content += `Status: ${titleContinuity.status}\n`;
      content += `Confidence: ${Math.round((titleContinuity.confidence || 0) * 100)}%\n`;
      content += `Chain Length: ${titleContinuity.chain_length} documents\n`;
      content += `Summary: ${titleContinuity.summary}\n\n`;
      
      // Chronology
      content += `Ownership Timeline:\n`;
      titleContinuity.chronology.forEach((entry, i) => {
        const status = entry.link_status === 'VALID' ? '✓' : 
                       entry.link_status === 'UNCERTAIN' ? '?' :
                       entry.link_status === 'NAME_MISMATCH' ? '✗' :
                       entry.link_status === 'START' ? '●' : '○';
        content += `  ${status} ${i + 1}. ${entry.doc_type_label} (${entry.doc_no || 'No number'}) - ${entry.date_display || 'Date unknown'}\n`;
        content += `      From: ${entry.from || 'Unknown'} → To: ${entry.to || 'Unknown'}\n`;
        if (entry.link_status === 'UNCERTAIN' || entry.link_status === 'NAME_MISMATCH') {
          content += `      ⚠ Expected: ${entry.expected_owner} (Match: ${Math.round((entry.name_match_score || 0) * 100)}%)\n`;
        }
      });
      
      // Issues
      if (titleContinuity.issues && titleContinuity.issues.length > 0) {
        content += `\nIssues Detected (${titleContinuity.issues.length}):\n`;
        titleContinuity.issues.forEach((issue, i) => {
          content += `  ${i + 1}. [${issue.severity}] ${issue.type}: ${issue.description}\n`;
          if (issue.advisory) {
            content += `      Advisory: ${issue.advisory}\n`;
          }
        });
      }
      content += `\n`;
    }
    
    content += `DOCUMENTS RECEIVED (${result.available_docs?.length || 0})\n`;
    content += `${'-'.repeat(50)}\n`;
    (result.available_docs || []).forEach((d, i) => {
      content += `${i + 1}. ${d.doc_type_label || d.doc_type} - ${d.file_name}\n`;
    });
    
    // Show all missing documents (Referenced - Uploaded = Missing)
    const missingDocsForTxt = result.missing_documents || [];
    
    content += `\n\nMISSING DOCUMENTS (${missingDocsForTxt.length})\n`;
    content += `${'-'.repeat(50)}\n`;
    
    // Group by risk level first, then by priority
    const critical = missingDocsForTxt.filter(m => m.risk_level === 'CRITICAL');
    const highRisk = missingDocsForTxt.filter(m => m.risk_level === 'HIGH');
    const mandatory = missingDocsForTxt.filter(m => m.priority === 'MANDATORY' && m.risk_level !== 'CRITICAL' && m.risk_level !== 'HIGH');
    const conditional = missingDocsForTxt.filter(m => m.priority === 'CONDITIONAL' && m.risk_level !== 'CRITICAL' && m.risk_level !== 'HIGH');
    const optional = missingDocsForTxt.filter(m => m.priority === 'OPTIONAL' && m.risk_level !== 'CRITICAL' && m.risk_level !== 'HIGH');
    
    // Helper to format document details
    const formatDocDetails = (m, i) => {
      let details = `   ${i + 1}. ${m.doc_type_label}\n`;
      
      // Add source
      const sourceLabels = {
        'ACT_ENGINE': 'Legal Act Requirement',
        'RULE_ENGINE': 'Standard Rule',
        'INTELLIGENT_ANALYSIS': 'Document Reference',
        'TITLE_CONTINUITY_ENGINE': 'Chain Advisory'
      };
      details += `      Source: ${sourceLabels[m.source] || m.source || 'Document Reference'}\n`;
      
      // Add act reference if present
      if (m.act_name) {
        details += `      Legal Basis: ${m.act_name}\n`;
      }
      
      // Add narration or reason
      if (m.narration) {
        details += `      ${m.narration}\n`;
      } else if (m.reason) {
        details += `      Reason: ${m.reason}\n`;
      }
      
      return details;
    };
    
    if (critical.length > 0) {
      content += `\n🔴 CRITICAL RISK (${critical.length})\n`;
      critical.forEach((m, i) => { content += formatDocDetails(m, i); });
    }
    
    if (highRisk.length > 0) {
      content += `\n🟠 HIGH RISK (${highRisk.length})\n`;
      highRisk.forEach((m, i) => { content += formatDocDetails(m, i); });
    }
    
    if (mandatory.length > 0) {
      content += `\n📋 MANDATORY (${mandatory.length})\n`;
      mandatory.forEach((m, i) => { content += formatDocDetails(m, i); });
    }
    
    if (conditional.length > 0) {
      content += `\n📋 CONDITIONAL (${conditional.length})\n`;
      conditional.forEach((m, i) => { content += formatDocDetails(m, i); });
    }
    
    if (optional.length > 0) {
      content += `\n🟢 OPTIONAL (${optional.length})\n`;
      optional.forEach((m, i) => { content += formatDocDetails(m, i); });
    }
    
    // Add missing docs without risk/priority (from 3-table model)
    const uncategorized = missingDocsForTxt.filter(m => 
      !m.risk_level && !m.priority
    );
    if (uncategorized.length > 0) {
      content += `\n📄 REFERENCED BUT NOT UPLOADED (${uncategorized.length})\n`;
      uncategorized.forEach((m, i) => {
        content += `   ${i + 1}. ${m.doc_type_label || m.doc_type}\n`;
        if (m.doc_no) content += `      Doc No: ${m.doc_no}\n`;
        if (m.date) content += `      Date: ${m.date}\n`;
        if (m.referenced_in) content += `      Referenced In: ${m.referenced_in}\n`;
      });
    }
    
    content += `\n\n${'='.repeat(50)}\n`;
    content += `Risk Score: ${riskScore}\n`;
    content += `Status: ${result.is_ready_for_opinion ? 'READY FOR OPINION' : 'INCOMPLETE'}\n`;
    
    // EC Reconciliation Report Section
    if (ecComparison && !ecComparison.no_ec_uploaded) {
      content += `\n\n${'='.repeat(50)}\n`;
      content += `EC-DOCUMENT RECONCILIATION REPORT\n`;
      content += `${'='.repeat(50)}\n`;
      content += `Source of Truth: Encumbrance Certificate (EC)\n\n`;
      
      // Section 1: EC Documents (Baseline)
      content += `1️⃣ EC DOCUMENTS (BASELINE)\n`;
      content += `${'-'.repeat(40)}\n`;
      if (ecComparison.ec_docs && ecComparison.ec_docs.length > 0) {
        ecComparison.ec_docs.forEach((doc, idx) => {
          content += `   ${doc.ec_ref}: ${doc.doc_type} ${doc.doc_no || ''}/${doc.year || ''} | SRO: ${doc.sro || '-'}\n`;
        });
        content += `   Total EC Documents: ${ecComparison.ec_docs_count || ecComparison.ec_docs.length}\n`;
      } else {
        content += `   No EC documents found\n`;
      }
      
      // Section 2: EC vs Uploaded
      content += `\n2️⃣ EC vs UPLOADED DOCUMENTS\n`;
      content += `${'-'.repeat(40)}\n`;
      if (ecComparison.ec_vs_uploaded) {
        const summary = ecComparison.ec_vs_uploaded.summary || {};
        content += `   EC Docs: ${summary.ec_docs_count || 0} | Uploaded: ${summary.uploaded_count || 0} | Matched: ${summary.matched_count || 0} | Unmatched: ${summary.unmatched_count || 0}\n\n`;
        
        if (ecComparison.ec_vs_uploaded.matched?.length > 0) {
          content += `   ✅ MATCHED:\n`;
          ecComparison.ec_vs_uploaded.matched.forEach(m => {
            content += `      ${m.ec_ref}: ${m.ec_document} → ${m.matched_with} (${m.confidence})\n`;
          });
        }
        
        if (ecComparison.ec_vs_uploaded.unmatched?.length > 0) {
          content += `   ⚠️ UNMATCHED:\n`;
          ecComparison.ec_vs_uploaded.unmatched.forEach(u => {
            content += `      ${u.ec_ref}: ${u.ec_document}\n`;
            if (u.candidate) content += `         Candidate: ${u.candidate}\n`;
            content += `         Reason: ${u.reason}\n`;
          });
        }
      }
      
      // Section 3: EC vs Referenced
      content += `\n3️⃣ EC vs REFERENCED DOCUMENTS\n`;
      content += `${'-'.repeat(40)}\n`;
      if (ecComparison.ec_vs_referenced) {
        const summary = ecComparison.ec_vs_referenced.summary || {};
        content += `   EC Docs: ${summary.ec_docs_count || 0} | Referenced: ${summary.referenced_count || 0} | Matched: ${summary.matched_count || 0} | Unmatched: ${summary.unmatched_count || 0}\n\n`;
        
        if (ecComparison.ec_vs_referenced.matched?.length > 0) {
          content += `   ✅ MATCHED:\n`;
          ecComparison.ec_vs_referenced.matched.forEach(m => {
            content += `      ${m.ec_ref}: ${m.ec_document} → Referenced in: ${m.referenced_in} (${m.match_status})\n`;
          });
        }
        
        if (ecComparison.ec_vs_referenced.referenced_not_in_ec?.length > 0) {
          content += `   🔴 REFERENCED BUT NOT IN EC:\n`;
          ecComparison.ec_vs_referenced.referenced_not_in_ec.forEach(r => {
            content += `      ${r.referenced_doc_type} (Referenced in: ${r.referenced_in})\n`;
          });
        }
      }
      
      // Section 4: Inference Summary
      content += `\n4️⃣ INFERENCE SUMMARY\n`;
      content += `${'-'.repeat(40)}\n`;
      if (ecComparison.inference) {
        content += `   Total EC Docs: ${ecComparison.inference.total_ec_docs || 0}\n`;
        content += `   Total Matched: ${ecComparison.inference.total_matched_count || 0}\n`;
        content += `   Total Unmatched: ${ecComparison.inference.total_unmatched_count || 0}\n\n`;
        
        if (ecComparison.inference.matched?.length > 0) {
          content += `   ✅ INFERRED MATCHED:\n`;
          ecComparison.inference.matched.forEach(m => {
            content += `      ${m.ec_ref}: ${m.document} (via ${m.matched_via})\n`;
          });
        }
        
        if (ecComparison.inference.unmatched?.length > 0) {
          content += `   ⚠️ INFERRED UNMATCHED:\n`;
          ecComparison.inference.unmatched.forEach(u => {
            content += `      ${u.ec_ref}: ${u.document}\n`;
          });
        }
      }
    } else if (ecComparison && ecComparison.no_ec_uploaded) {
      content += `\n\n${'='.repeat(50)}\n`;
      content += `EC-DOCUMENT RECONCILIATION REPORT\n`;
      content += `${'='.repeat(50)}\n`;
      content += `⚠️ No EC document was uploaded. EC reconciliation was skipped.\n`;
    }
    
    // Download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missing_docs_checklist_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Reset
  const resetForm = () => {
    setStep(1);
    setUploadedFiles([]);
    setResult(null);
    setCaseName('');
    setFlags([]);
    setError(null);
    setChainAnalysis(null);
    setTriggeredActs([]);
    setRiskScore('NONE');
    setTitleContinuity(null);
    setEcComparison(null);  // Reset EC Comparison
    setActiveResultTab('title');
  };

  // Show all missing documents - don't filter by doc_no
  // Missing documents are determined by: Referenced - Uploaded = Missing
  const filteredMissingDocs = useMemo(() => {
    if (!result?.missing_documents) return [];
    return result.missing_documents;
  }, [result?.missing_documents]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="missing-docs-page">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                📋 Missing Document Identifier
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              Telangana Legal Document Checklist
            </div>
          </div>
        </div>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">×</button>
          </div>
        </div>
      )}
      
      {/* Progress Steps */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center space-x-4 mb-6">
          {[
            { num: 1, label: 'Upload Documents', icon: Upload },
            { num: 2, label: 'Configure Check', icon: FileText },
            { num: 3, label: 'View Results', icon: CheckCircle }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div 
                className={`flex items-center space-x-2 ${step >= s.num ? 'text-blue-600' : 'text-gray-400'}`}
                data-testid={`step-${s.num}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium hidden md:inline">{s.label}</span>
              </div>
              {idx < 2 && <div className={`w-12 md:w-20 h-0.5 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="step-1-upload">
            <h2 className="text-lg font-semibold mb-4">Step 1: Upload Scanned Documents</h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload your property documents. The system will auto-detect document types from filenames.
              You can override the detected type if needed.
            </p>
            
            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              <FolderOpen className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 mb-2">Drag and drop files here, or click to select</p>
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.tiff"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                id="file-upload"
                data-testid="file-input"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Select Files
              </label>
              <p className="text-xs text-gray-400 mt-2">Supported: PDF, JPG, PNG, TIFF</p>
            </div>
            
            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Uploaded Documents ({uploadedFiles.length})
                  </h3>
                  <button
                    onClick={classifyWithAI}
                    disabled={classifying || uploadedFiles.length === 0}
                    className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      classifying 
                        ? 'bg-purple-100 text-purple-400 cursor-wait' 
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                    data-testid="ai-classify-btn"
                  >
                    {classifying ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        {classifyProgress.total > 0 
                          ? `${classifyProgress.current}/${classifyProgress.total} docs...`
                          : 'Reading Documents...'
                        }
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        AI Classify (Read Content)
                      </>
                    )}
                  </button>
                </div>
                
                {/* Progress bar for AI classification - one file at a time */}
                {classifying && classifyProgress.total > 0 && (
                  <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between text-sm text-purple-700 mb-2">
                      <span className="font-medium">{classifyProgress.status}</span>
                      <span className="font-bold">{classifyProgress.current}/{classifyProgress.total}</span>
                    </div>
                    {classifyProgress.fileName && (
                      <div className="text-xs text-purple-600 mb-2 truncate flex items-center">
                        <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{classifyProgress.fileName}</span>
                      </div>
                    )}
                    <div className="w-full bg-purple-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(classifyProgress.current / classifyProgress.total) * 100}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-purple-500 text-center">
                      ⏳ AI classification may take 30-60 seconds per document...
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mb-3">
                  💡 Click &quot;AI Classify&quot; to automatically detect document types by reading content (more accurate than filename detection)
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {uploadedFiles.map((f, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      data-testid={`uploaded-file-${idx}`}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{f.file_name}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <ConfidenceBadge confidence={f.confidence} />
                            {f.classified_by === 'AI' && (
                              <span className="text-xs text-purple-600 font-medium flex items-center">
                                <Brain className="w-3 h-3 mr-1" /> AI
                              </span>
                            )}
                            {f.classified_by === 'FAILED' && (
                              <span className="text-xs text-red-500 font-medium">⚠ Failed</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <select
                          value={f.doc_type}
                          onChange={(e) => updateDocType(idx, e.target.value)}
                          className="text-sm border rounded px-2 py-1 bg-white max-w-[200px]"
                          data-testid={`doc-type-select-${idx}`}
                        >
                          {DOC_CATEGORIES.map(cat => (
                            <optgroup key={cat.name} label={`${cat.icon} ${cat.label}`}>
                              {docTypesByCategory[cat.name]?.map(dt => (
                                <option key={dt.value} value={dt.value}>{dt.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`remove-file-${idx}`}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Next Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={uploadedFiles.length === 0}
                className={`px-6 py-2 rounded-lg font-medium flex items-center ${
                  uploadedFiles.length > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="next-to-step-2-btn"
              >
                Next: Configure Check →
              </button>
            </div>
          </div>
        )}
        
        {/* Step 2: Configure */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="step-2-configure">
            <h2 className="text-lg font-semibold mb-4">Step 2: Configure Check</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type
                </label>
                <div className="space-y-2">
                  {PROPERTY_TYPES.map(pt => (
                    <label key={pt.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="propertyType"
                        value={pt.value}
                        checked={propertyType === pt.value}
                        onChange={(e) => setPropertyType(e.target.value)}
                        className="text-blue-600"
                        data-testid={`property-type-${pt.value}`}
                      />
                      <span className="text-sm">{pt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Profile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Checklist Profile
                </label>
                <div className="space-y-2">
                  {PROFILE_TYPES.map(p => (
                    <label key={p.value} className="flex items-start space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="profile"
                        value={p.value}
                        checked={profile === p.value}
                        onChange={(e) => setProfile(e.target.value)}
                        className="text-blue-600 mt-1"
                        data-testid={`profile-${p.value}`}
                      />
                      <div>
                        <span className="text-sm font-medium">{p.label}</span>
                        <p className="text-xs text-gray-500">{p.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Special Flags */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Conditions (if applicable)
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={flags.includes('DEATH_IN_CHAIN')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFlags([...flags, 'DEATH_IN_CHAIN']);
                      } else {
                        setFlags(flags.filter(f => f !== 'DEATH_IN_CHAIN'));
                      }
                    }}
                    className="text-blue-600"
                    data-testid="flag-death-in-chain"
                  />
                  <span className="text-sm">Death in ownership chain</span>
                </label>
              </div>
            </div>
            
            {/* Intelligent Analysis Toggle */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-purple-900">🧠 Intelligent Document Analysis</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Reads document content to find ALL referenced documents including:
                      </p>
                      <ul className="text-xs text-purple-600 mt-2 space-y-1 ml-4 list-disc">
                        <li>Link documents (prior sale deeds, partition deeds)</li>
                        <li>Permissions (Layout approval, Building permission, OC)</li>
                        <li>Certificates (EC, LRS, Legal Heir)</li>
                        <li>NOCs (Fire, Airport, Environment)</li>
                        <li>Powers (GPA, SPA) & Agreements</li>
                      </ul>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={useIntelligentAnalysis}
                        onChange={(e) => setUseIntelligentAnalysis(e.target.checked)}
                        className="sr-only peer"
                        data-testid="intelligent-analysis-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  {useIntelligentAnalysis && (
                    <div className="mt-3 flex items-center text-xs text-purple-800 bg-purple-100 rounded px-2 py-1">
                      <Zap className="w-3 h-3 mr-1" />
                      AI-powered analysis enabled - will extract references from recitals & whereas clauses
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Documents to Check ({uploadedFiles.length})</h3>
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map((f, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white border rounded text-xs">
                    {f.doc_type_label}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center"
                data-testid="back-to-step-1-btn"
              >
                ← Back
              </button>
              <button
                onClick={runCheck}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                data-testid="run-check-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {uploadProgress.status || 'Checking...'}
                  </>
                ) : (
                  <>
                    🔍 Identify Missing Documents
                  </>
                )}
              </button>
            </div>
            
            {/* Upload Progress Bar - One file at a time */}
            {loading && uploadProgress.total > 0 && (
              <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200" data-testid="upload-progress">
                <div className="flex items-center justify-between text-sm text-blue-700 mb-2">
                  <span className="font-medium">{uploadProgress.status}</span>
                  <span className="font-bold">{uploadProgress.current}/{uploadProgress.total}</span>
                </div>
                {uploadProgress.fileName && (
                  <div className="text-xs text-blue-600 mb-2 truncate flex items-center">
                    <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{uploadProgress.fileName}</span>
                  </div>
                )}
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-blue-500 text-center">
                  {uploadProgress.current < uploadProgress.total 
                    ? '⏳ Processing one document at a time for reliable analysis...'
                    : '✅ All documents processed!'
                  }
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Step 3: Results */}
        {step === 3 && result && (
          <div className="space-y-6" data-testid="step-3-results">
            {/* Summary Card with Risk Score */}
            <div className={`rounded-lg shadow-sm border p-6 ${
              result.is_ready_for_opinion ? 'bg-green-50 border-green-200' : 
              riskScore === 'CRITICAL' ? 'bg-red-50 border-red-200' :
              riskScore === 'HIGH' ? 'bg-orange-50 border-orange-200' : 'bg-white'
            }`}>
              {/* Golden Sentence */}
              <p className="text-sm text-gray-600 mb-4 italic border-l-4 border-blue-400 pl-3">
                {result.system_description || "The system only reports documents expressly referred to in the title deeds and identifies which of those are not furnished."}
              </p>
              
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  {result.is_ready_for_opinion ? '✅ All Referenced Documents Furnished' : '📊 Document Accounting Result'}
                </h2>
                <div className="flex items-center space-x-2">
                  {riskScore !== 'NONE' && <RiskBadge risk={riskScore} />}
                  <StatusBadge status={result.is_ready_for_opinion ? 'READY_FOR_OPINION' : 'INCOMPLETE'} />
                </div>
              </div>
              
              {/* Summary Stats - 3 Table Model */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-3xl font-bold text-blue-600">{result.referenced_documents?.length || 0}</div>
                  <div className="text-xs text-blue-700 font-medium">TABLE 1: Referenced</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-3xl font-bold text-green-600">{result.available_docs?.length || 0}</div>
                  <div className="text-xs text-green-700 font-medium">TABLE 2: Uploaded</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-3xl font-bold text-red-600">{filteredMissingDocs.length}</div>
                  <div className="text-xs text-red-700 font-medium">TABLE 3: Missing</div>
                </div>
              </div>
            </div>
            
            {/* ============================================================ */}
            {/* TABLE 1: Documents Referenced in Title                        */}
            {/* ============================================================ */}
            {result.referenced_documents && result.referenced_documents.length > 0 && (
              <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-6">
                <h3 className="text-md font-semibold text-blue-800 mb-4 flex items-center">
                  📋 TABLE 1: Documents Referenced in Title ({result.referenced_documents.length})
                </h3>
                <p className="text-xs text-blue-600 mb-3">
                  These documents are expressly mentioned in the uploaded deeds.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-100 text-blue-800">
                      <tr>
                        <th className="px-3 py-2 text-left">Doc Type</th>
                        <th className="px-3 py-2 text-left">Doc No</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Parties</th>
                        <th className="px-3 py-2 text-left">Mentioned In</th>
                        <th className="px-3 py-2 text-left">Narration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-100">
                      {result.referenced_documents.map((ref, idx) => (
                        <tr key={idx} className="bg-white hover:bg-blue-50">
                          <td className="px-3 py-2 font-medium">{ref.doc_type_label}</td>
                          <td className="px-3 py-2">{ref.doc_no || '-'}</td>
                          <td className="px-3 py-2">{ref.date || '-'}</td>
                          <td className="px-3 py-2 text-xs">
                            {ref.parties?.from && ref.parties?.to 
                              ? `${ref.parties.from} → ${ref.parties.to}` 
                              : '-'}
                          </td>
                          <td className="px-3 py-2 text-xs text-purple-600">
                            {ref.source_deed}
                            {ref.source_field && <span className="block text-gray-400">{ref.source_field}</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={ref.narration}>
                            {ref.narration}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* ============================================================ */}
            {/* TABLE 2: Uploaded Documents                                   */}
            {/* ============================================================ */}
            {result.available_docs && result.available_docs.length > 0 && (
              <div className="bg-green-50 rounded-lg shadow-sm border border-green-200 p-6">
                <h3 className="text-md font-semibold text-green-800 mb-4 flex items-center">
                  📂 TABLE 2: Uploaded Documents ({result.available_docs.length})
                </h3>
                <p className="text-xs text-green-600 mb-3">
                  Documents you have uploaded for this check.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-100 text-green-800">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Doc Type</th>
                        <th className="px-3 py-2 text-left">File Name</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-100">
                      {result.available_docs.map((doc, idx) => (
                        <tr key={idx} className="bg-white hover:bg-green-50">
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium">{doc.doc_type_label || doc.doc_type}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{doc.file_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* ============================================================ */}
            {/* TABLE 3: Missing Documents (Referenced - Uploaded)           */}
            {/* ============================================================ */}
            {filteredMissingDocs.length > 0 && (
              <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6">
                <h3 className="text-md font-semibold text-red-800 mb-4 flex items-center">
                  ❌ TABLE 3: Missing Documents ({filteredMissingDocs.length})
                </h3>
                <p className="text-xs text-red-600 mb-3">
                  Documents referenced in title (TABLE 1) but not found in uploaded documents (TABLE 2).
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 text-red-800">
                      <tr>
                        <th className="px-3 py-2 text-left">Doc Type</th>
                        <th className="px-3 py-2 text-left">Doc No</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Referenced In</th>
                        <th className="px-3 py-2 text-left">Narration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {filteredMissingDocs.map((doc, idx) => (
                        <tr key={idx} className="bg-white hover:bg-red-50">
                          <td className="px-3 py-2 font-medium">{doc.doc_type_label}</td>
                          <td className="px-3 py-2">{doc.doc_no || '-'}</td>
                          <td className="px-3 py-2">{doc.date || '-'}</td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                              {doc.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-purple-600">{doc.referenced_in}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={doc.narration}>
                            {doc.narration}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Triggered Acts Section */}
            {triggeredActs.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow-sm border border-amber-200 p-6">
                <h3 className="text-md font-semibold mb-4 text-amber-800 flex items-center">
                  ⚖️ Legal Acts Triggered ({triggeredActs.length})
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  The following legal requirements apply based on document content analysis:
                </p>
                <div className="space-y-3">
                  {triggeredActs.map((act, idx) => {
                    // Debug: log trigger_details for each act
                    console.log(`Act ${idx}: ${act.act_name}`, { 
                      trigger_details: act.trigger_details, 
                      trigger_reason: act.trigger_reason 
                    });
                    
                    const hasTriggerDetails = act.trigger_details && 
                      (act.trigger_details.trigger_word || act.trigger_details.narration || act.trigger_details.doc_type_label);
                    
                    return (
                    <div key={idx} className="p-4 bg-white rounded-lg border border-amber-100 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-amber-900">{act.act_name}</span>
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                          {act.category}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{act.obligation}</p>
                      
                      {/* Enhanced Trigger Details: doc_type, doc_no, date, narration */}
                      {hasTriggerDetails && (
                        <div className="bg-purple-50 rounded-md p-3 mt-2 border border-purple-100">
                          <p className="text-xs font-semibold text-purple-800 mb-2">📄 Trigger Details:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {act.trigger_details.doc_type_label && (
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-1">Document Type:</span>
                                <span className="text-purple-700 font-medium">{act.trigger_details.doc_type_label}</span>
                              </div>
                            )}
                            {act.trigger_details.doc_no && (
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-1">Document No:</span>
                                <span className="text-purple-700 font-medium">{act.trigger_details.doc_no}</span>
                              </div>
                            )}
                            {act.trigger_details.date && (
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-1">Date:</span>
                                <span className="text-purple-700 font-medium">{act.trigger_details.date}</span>
                              </div>
                            )}
                            {act.trigger_details.trigger_word && (
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-1">Trigger Word:</span>
                                <span className="text-purple-700 font-medium bg-purple-100 px-1 rounded">&ldquo;{act.trigger_details.trigger_word}&rdquo;</span>
                              </div>
                            )}
                            {act.trigger_details.source_file && (
                              <div className="flex items-center col-span-2">
                                <span className="text-gray-500 mr-1">Source File:</span>
                                <span className="text-purple-700 font-medium">{act.trigger_details.source_file}</span>
                              </div>
                            )}
                          </div>
                          {act.trigger_details.narration && (
                            <div className="mt-3 pt-2 border-t border-purple-200">
                              <span className="text-gray-500 text-xs font-medium">Narration:</span>
                              <p className="text-xs text-purple-800 italic mt-1 bg-white p-2 rounded border border-purple-100">
                                {act.trigger_details.narration}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Fallback for legacy trigger_reason when no trigger_details */}
                      {!hasTriggerDetails && act.trigger_reason && (
                        <p className="text-xs text-purple-600 mt-1 italic">
                          📄 Triggered by: {act.trigger_reason}
                        </p>
                      )}
                    </div>
                  );
                  })}
                </div>
              </div>
            )}
            
            {/* ============================================================ */}
            {/* DUAL-TREE TITLE CONTINUITY ENGINE v3.0                        */}
            {/* Tree A: Deed-Based Title Chain (Ownership)                    */}
            {/* Tree B: Revenue-Based Title Chain (Possession)                */}
            {/* Combined View: Observations + Risk Flags                      */}
            {/* ============================================================ */}
            {titleContinuity && (titleContinuity.deed_tree?.length > 0 || titleContinuity.chain_length > 0) && (
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg shadow-sm border border-cyan-200 p-6" data-testid="dual-tree-section">
                {/* Header with Verdict */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-semibold text-cyan-800 flex items-center">
                    🌳 Title Continuity Analysis (Dual-Tree Engine v3.0)
                  </h3>
                  <div className="flex items-center space-x-3">
                    {titleContinuity.risk_score && (
                      <RiskBadge risk={titleContinuity.risk_score} />
                    )}
                    <ChainStatusBadge status={titleContinuity.status} confidence={titleContinuity.confidence || 0} />
                    <span className="text-xs text-gray-500">
                      Confidence: {Math.round((titleContinuity.confidence || 0) * 100)}%
                    </span>
                  </div>
                </div>
                
                {/* Summary */}
                <p className="text-sm text-cyan-700 mb-4">
                  {titleContinuity.summary}
                </p>
                
                {/* Dual-Tree Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">{titleContinuity.deed_tree?.length || titleContinuity.chain_length || 0}</div>
                    <div className="text-xs text-emerald-700 font-medium">📜 TREE A: Deeds</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{titleContinuity.revenue_tree?.length || 0}</div>
                    <div className="text-xs text-amber-700 font-medium">📋 TREE B: Revenue</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{titleContinuity.combined_timeline?.length || 0}</div>
                    <div className="text-xs text-blue-700 font-medium">📅 Timeline Events</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">{titleContinuity.observations?.length || 0}</div>
                    <div className="text-xs text-purple-700 font-medium">🔍 Observations</div>
                  </div>
                </div>
                
                {/* TREE A: Deed-Based Title Chain */}
                {titleContinuity.deed_tree && titleContinuity.deed_tree.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center">
                      📜 TREE A: Deed-Based Title Chain
                      <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Ownership</span>
                    </h4>
                    <p className="text-xs text-emerald-600 mb-3 italic">
                      &quot;How did ownership legally move?&quot; - Ordered by document execution/registration date
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-emerald-100 text-emerald-800">
                          <tr>
                            <th className="px-3 py-2 text-left">Year</th>
                            <th className="px-3 py-2 text-left">Deed Type</th>
                            <th className="px-3 py-2 text-left">From → To</th>
                            <th className="px-3 py-2 text-left">Doc Ref</th>
                            <th className="px-3 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-100">
                          {titleContinuity.deed_tree.map((node, idx) => (
                            <tr key={idx} className={`bg-white hover:bg-emerald-50 ${
                              node.validation_status === 'BREAK' ? 'bg-red-50' :
                              node.validation_status === 'EXPLAINABLE' ? 'bg-yellow-50' :
                              node.validation_status === 'ENCUMBRANCE' ? 'bg-blue-50' :
                              node.is_statutory ? 'bg-blue-50' :
                              ''
                            }`}>
                              <td className="px-3 py-2 font-medium">{node.year || '-'}</td>
                              <td className="px-3 py-2">
                                <span>{node.deed_type}</span>
                                {(node.is_statutory || node.validation_status === 'ENCUMBRANCE') && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Statutory</span>
                                )}
                                {node.branch_id && node.branch_id !== 'trunk' && (
                                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                    {node.branch_id.replace('TRUNK_', '').substring(0, 15)}...
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                <span className="text-gray-600">{node.from_owner || 'Unknown'}</span>
                                <span className="mx-1">→</span>
                                <span className="text-emerald-700 font-medium">{node.to_owner || 'Unknown'}</span>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500">{node.document_ref || '-'}</td>
                              <td className="px-3 py-2">
                                <LinkStatusBadge status={node.validation_status} />
                                {node.link_reason && (
                                  <span className="ml-1 text-xs text-gray-500">{node.link_reason}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* TREE B: Revenue-Based Title Chain */}
                {titleContinuity.revenue_tree && titleContinuity.revenue_tree.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center">
                      📋 TREE B: Revenue-Based Title Chain
                      <span className="ml-2 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Possession</span>
                    </h4>
                    <p className="text-xs text-amber-600 mb-3 italic">
                      &quot;Who was shown in possession?&quot; - Ordered by revenue year/mutation date
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-100 text-amber-800">
                          <tr>
                            <th className="px-3 py-2 text-left">Year</th>
                            <th className="px-3 py-2 text-left">Record Type</th>
                            <th className="px-3 py-2 text-left">Owner Shown</th>
                            <th className="px-3 py-2 text-left">Survey No</th>
                            <th className="px-3 py-2 text-left">Extent</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {titleContinuity.revenue_tree.map((node, idx) => (
                            <tr key={idx} className="bg-white hover:bg-amber-50">
                              <td className="px-3 py-2 font-medium">{node.year || '-'}</td>
                              <td className="px-3 py-2">{node.record_type}</td>
                              <td className="px-3 py-2 text-amber-700 font-medium">{node.owner_shown || 'Unknown'}</td>
                              <td className="px-3 py-2 text-xs text-gray-500">{node.survey_no || '-'}</td>
                              <td className="px-3 py-2 text-xs text-gray-500">{node.extent || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Combined Timeline View */}
                {titleContinuity.combined_timeline && titleContinuity.combined_timeline.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                      📅 Combined Chronological Timeline
                      <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Merged View</span>
                    </h4>
                    <p className="text-xs text-blue-600 mb-3 italic">
                      Both deed and revenue records merged for side-by-side comparison
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {titleContinuity.combined_timeline.map((entry, idx) => (
                        <div key={idx} className={`flex items-center space-x-3 p-2 rounded border ${
                          entry.source === 'DEED' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                        }`}>
                          <span className="text-xs font-medium w-12 text-gray-700">{entry.year || '-'}</span>
                          <TreeSourceBadge source={entry.source} />
                          <span className="text-xs flex-1">{entry.description}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            entry.status === 'VALID' || entry.status === 'START' ? 'bg-green-100 text-green-700' :
                            entry.status === 'BREAK' ? 'bg-red-100 text-red-700' :
                            entry.status === 'EXPLAINABLE' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {entry.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Observations Panel */}
                {titleContinuity.observations && titleContinuity.observations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center">
                      🔍 Observations & Risk Analysis
                      <span className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Intelligence Layer</span>
                    </h4>
                    <p className="text-xs text-purple-600 mb-3 italic">
                      GUARDRAIL: Revenue Tree NEVER repairs a Deed Break. Good revenue continuity can reduce risk, but can NEVER cure a title defect.
                    </p>
                    <div className="space-y-2">
                      {titleContinuity.observations.map((obs, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${
                          obs.risk_level === 'CRITICAL' ? 'bg-red-50 border-red-200' :
                          obs.risk_level === 'HIGH' ? 'bg-orange-50 border-orange-200' :
                          obs.risk_level === 'MEDIUM' ? 'bg-yellow-50 border-yellow-200' :
                          obs.type === 'HEALTHY' ? 'bg-green-50 border-green-200' :
                          'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-2">
                              <ObservationBadge type={obs.type} riskLevel={obs.risk_level} />
                              <span className="text-xs text-gray-500">{obs.category}</span>
                              {obs.year && <span className="text-xs text-gray-400">({obs.year})</span>}
                            </div>
                            <RiskBadge risk={obs.risk_level} />
                          </div>
                          <p className="text-sm text-gray-700">{obs.description}</p>
                          {obs.recommendation && (
                            <p className="text-xs text-blue-600 mt-1 italic">💡 {obs.recommendation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Legacy Chronology (Fallback for old data format) */}
                {(!titleContinuity.deed_tree || titleContinuity.deed_tree.length === 0) && titleContinuity.chronology && titleContinuity.chronology.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-cyan-800 mb-3">📜 Ownership Timeline</h4>
                    <div className="min-w-fit">
                      {titleContinuity.chronology.map((entry, idx) => {
                        const isLast = idx === titleContinuity.chronology.length - 1;
                        const nodeColorClass = 
                          entry.link_status === 'NAME_MISMATCH' ? 'bg-red-500' :
                          entry.link_status === 'UNCERTAIN' ? 'bg-yellow-500' :
                          entry.link_status === 'VALID' || entry.link_status === 'START' ? 'bg-green-500' :
                          'bg-gray-400';
                        
                        return (
                          <div key={idx} className="flex items-start">
                            <div className="flex flex-col items-center mr-4">
                              <div className={`w-4 h-4 rounded-full ${nodeColorClass}`}></div>
                              {!isLast && (
                                <div className={`w-0.5 h-16 ${
                                  entry.link_status === 'NAME_MISMATCH' ? 'bg-red-300 border-dashed' :
                                  entry.link_status === 'UNCERTAIN' ? 'bg-yellow-300' :
                                  'bg-green-300'
                                }`}></div>
                              )}
                            </div>
                            <div className="flex-1 pb-4">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-sm">{entry.doc_type_label}</span>
                                <span className="text-xs text-gray-500">{entry.date_display || 'Date unknown'}</span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <span>From: {entry.from || 'Unknown'}</span>
                                <span className="mx-2">→</span>
                                <span>To: {entry.to || 'Unknown'}</span>
                              </div>
                              {entry.link_status === 'NAME_MISMATCH' && (
                                <div className="text-xs text-red-600 mt-1">
                                  ⚠️ Expected: {entry.expected_owner}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Issues List */}
                {titleContinuity.issues && titleContinuity.issues.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-cyan-200">
                    <h4 className="text-sm font-semibold text-cyan-800 mb-3">
                      ⚠️ Issues Detected ({titleContinuity.issues.length})
                    </h4>
                    <div className="space-y-2">
                      {titleContinuity.issues.map((issue, idx) => (
                        <div key={idx} className={`p-3 rounded-lg text-sm ${
                          issue.severity === 'CRITICAL' ? 'bg-red-100 border border-red-300' :
                          issue.severity === 'HIGH' ? 'bg-red-50 border border-red-200' :
                          issue.severity === 'MEDIUM' ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">
                              {issue.type === 'DEED_BREAK' ? '🔴 Deed Break (Critical)' :
                               issue.type === 'CHAIN_BREAK' ? '🔴 Chain Break' :
                               issue.type === 'EXPLAINABLE_LINK' ? '🟡 Needs Supporting Doc' :
                               issue.type === 'NEEDS_SUPPORTING_DOC' ? '🟡 Needs Supporting Doc' :
                               issue.type === 'TIME_GAP' ? '🟠 Time Gap' :
                               issue.type === 'INCOMPLETE_DATA' ? '⚪ Incomplete Data' :
                               issue.type}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              issue.severity === 'CRITICAL' ? 'bg-red-200 text-red-800' :
                              issue.severity === 'HIGH' ? 'bg-red-100 text-red-700' :
                              issue.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {issue.severity}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{issue.description}</p>
                          {issue.advisory && (
                            <p className="text-xs text-blue-600 mt-1 italic">💡 {issue.advisory}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Legend */}
                <div className="mt-4 pt-3 border-t border-cyan-200 flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div> Valid/Continuous</div>
                  <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></div> Explainable/Needs Doc</div>
                  <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div> Break/Critical</div>
                  <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div> Start Node</div>
                </div>
              </div>
            )}
            {/* Chain Analysis Results (if intelligent analysis was used) */}
            {chainAnalysis && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow-sm border border-purple-200 p-6">
                <h3 className="text-md font-semibold mb-4 text-purple-800 flex items-center">
                  <Brain className="w-5 h-5 mr-2" />
                  🧠 Intelligent Chain-of-Title Analysis
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-purple-600">{chainAnalysis.total_references_found}</div>
                    <div className="text-xs text-gray-500">References Found</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-green-600">{chainAnalysis.matched_references?.length || 0}</div>
                    <div className="text-xs text-gray-500">Matched in Uploads</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-red-600">{chainAnalysis.missing_references?.length || 0}</div>
                    <div className="text-xs text-gray-500">Missing References</div>
                  </div>
                </div>
                
                {chainAnalysis.summary && (
                  <div className="p-3 bg-white rounded-lg border text-sm whitespace-pre-line">
                    {chainAnalysis.summary}
                  </div>
                )}
                
                {/* Show missing references from document content */}
                {chainAnalysis.missing_references?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-purple-800 mb-2">📄 Referenced but NOT uploaded:</h4>
                    <div className="space-y-2">
                      {chainAnalysis.missing_references.map((ref, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-lg border border-purple-100">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-purple-900">{ref.doc_type_label}</span>
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              {ref.confidence} confidence
                            </span>
                          </div>
                          {ref.doc_number && (
                            <p className="text-xs text-gray-600">Doc No: {ref.doc_number}</p>
                          )}
                          {ref.found_in && (
                            <p className="text-xs text-gray-500 mt-1">Found in: {ref.found_in}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* ============================================================ */}
            {/* EC-DOCUMENT RECONCILIATION REPORT                            */}
            {/* Golden Rule: Only EC is the baseline. Everything else is     */}
            {/* a comparison.                                                 */}
            {/* ============================================================ */}
            {ecComparison && (
              <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg shadow-sm border border-slate-200 p-6" data-testid="ec-reconciliation-section">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                    🔍 EC–Document Reconciliation Report
                  </h3>
                  <span className="text-xs text-slate-500 italic">Source of Truth: Encumbrance Certificate (EC)</span>
                </div>
                
                {/* NO EC UPLOADED WARNING */}
                {ecComparison.no_ec_uploaded && (
                  <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl">⚠️</span>
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-2">No Encumbrance Certificate (EC) Uploaded</h4>
                        <p className="text-sm text-amber-700 mb-3">
                          EC-Document Reconciliation requires an Encumbrance Certificate document as the baseline.
                        </p>
                        <p className="text-xs text-amber-600 font-medium">
                          💡 Upload the EC document and ensure it's classified as "Encumbrance Certificate" (EC)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* ======================================== */}
                {/* SECTION 1: EC Documents (Baseline)       */}
                {/* ======================================== */}
                {!ecComparison.no_ec_uploaded && ecComparison.ec_docs && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-slate-700 mb-2 flex items-center">
                      1️⃣ EC Documents (Baseline)
                      <span className="ml-2 px-2 py-0.5 text-xs bg-slate-200 text-slate-700 rounded-full">
                        {ecComparison.ec_docs_count || ecComparison.ec_docs?.length || 0} docs
                      </span>
                    </h4>
                    <p className="text-xs text-slate-500 mb-3 italic">
                      List of documents officially recorded in the Encumbrance Certificate
                    </p>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                        <thead className="bg-slate-100 text-slate-700">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">EC Ref</th>
                            <th className="px-3 py-2 text-left font-medium">Doc Type</th>
                            <th className="px-3 py-2 text-left font-medium">Doc No</th>
                            <th className="px-3 py-2 text-left font-medium">Year</th>
                            <th className="px-3 py-2 text-left font-medium">SRO</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {ecComparison.ec_docs?.map((doc, idx) => (
                            <tr key={idx} className="bg-white hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium text-indigo-600">{doc.ec_ref}</td>
                              <td className="px-3 py-2">{doc.doc_type}</td>
                              <td className="px-3 py-2">{doc.doc_no || '-'}</td>
                              <td className="px-3 py-2">{doc.year || '-'}</td>
                              <td className="px-3 py-2 text-xs">{doc.sro || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      <strong>Total EC Documents:</strong> {ecComparison.ec_docs_count || ecComparison.ec_docs?.length || 0}
                    </p>
                  </div>
                )}
                
                {/* ======================================== */}
                {/* SECTION 2: EC vs Uploaded Documents      */}
                {/* ======================================== */}
                {!ecComparison.no_ec_uploaded && ecComparison.ec_vs_uploaded && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-slate-700 mb-2">
                      2️⃣ EC vs Uploaded Documents
                    </h4>
                    <p className="text-xs text-slate-500 mb-3 italic">
                      Comparison between EC-listed documents and uploaded files
                    </p>
                    
                    {/* Summary Table */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="text-center p-3 bg-slate-100 rounded-lg">
                        <div className="text-xl font-bold text-slate-700">{ecComparison.ec_vs_uploaded.summary?.ec_docs_count || 0}</div>
                        <div className="text-xs text-slate-600">EC Documents</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-700">{ecComparison.ec_vs_uploaded.summary?.uploaded_count || 0}</div>
                        <div className="text-xs text-blue-600">Uploaded Files</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-700">{ecComparison.ec_vs_uploaded.summary?.matched_count || 0}</div>
                        <div className="text-xs text-green-600">Matched</div>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <div className="text-xl font-bold text-amber-700">{ecComparison.ec_vs_uploaded.summary?.unmatched_count || 0}</div>
                        <div className="text-xs text-amber-600">Unmatched</div>
                      </div>
                    </div>
                    
                    {/* Matched (EC ↔ Uploaded) */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-green-700 mb-2">✅ Matched (EC ↔ Uploaded)</h5>
                      {ecComparison.ec_vs_uploaded.matched?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-green-200 rounded-lg overflow-hidden">
                            <thead className="bg-green-50 text-green-800">
                              <tr>
                                <th className="px-3 py-2 text-left">EC Ref</th>
                                <th className="px-3 py-2 text-left">EC Document</th>
                                <th className="px-3 py-2 text-left">Matched With</th>
                                <th className="px-3 py-2 text-left">Confidence</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-green-100">
                              {ecComparison.ec_vs_uploaded.matched.map((m, idx) => (
                                <tr key={idx} className="bg-white">
                                  <td className="px-3 py-2 font-medium text-indigo-600">{m.ec_ref}</td>
                                  <td className="px-3 py-2">{m.ec_document}</td>
                                  <td className="px-3 py-2 text-blue-600">{m.matched_with}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                      m.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                      m.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>{m.confidence}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic p-2 bg-slate-50 rounded">None</p>
                      )}
                    </div>
                    
                    {/* Unmatched (EC ↔ Uploaded) */}
                    <div>
                      <h5 className="text-sm font-medium text-amber-700 mb-2">⚠️ Unmatched (EC ↔ Uploaded)</h5>
                      {ecComparison.ec_vs_uploaded.unmatched?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-amber-200 rounded-lg overflow-hidden">
                            <thead className="bg-amber-50 text-amber-800">
                              <tr>
                                <th className="px-3 py-2 text-left">EC Ref</th>
                                <th className="px-3 py-2 text-left">EC Document</th>
                                <th className="px-3 py-2 text-left">Uploaded Candidate</th>
                                <th className="px-3 py-2 text-left">Reason</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100">
                              {ecComparison.ec_vs_uploaded.unmatched.map((u, idx) => (
                                <tr key={idx} className="bg-white">
                                  <td className="px-3 py-2 font-medium text-indigo-600">{u.ec_ref}</td>
                                  <td className="px-3 py-2">{u.ec_document}</td>
                                  <td className="px-3 py-2 text-blue-600">{u.candidate || '-'}</td>
                                  <td className="px-3 py-2 text-xs text-slate-600">{u.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic p-2 bg-slate-50 rounded">None</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* ======================================== */}
                {/* SECTION 3: EC vs Referenced Documents    */}
                {/* ======================================== */}
                {!ecComparison.no_ec_uploaded && ecComparison.ec_vs_referenced && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-slate-700 mb-2">
                      3️⃣ EC vs Referenced Documents
                    </h4>
                    <p className="text-xs text-slate-500 mb-3 italic">
                      Comparison between EC-listed documents and documents referenced inside deeds
                    </p>
                    
                    {/* Summary */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="text-center p-3 bg-slate-100 rounded-lg">
                        <div className="text-xl font-bold text-slate-700">{ecComparison.ec_vs_referenced.summary?.ec_docs_count || 0}</div>
                        <div className="text-xs text-slate-600">EC Documents</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-xl font-bold text-purple-700">{ecComparison.ec_vs_referenced.summary?.referenced_count || 0}</div>
                        <div className="text-xs text-purple-600">Referenced</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-700">{ecComparison.ec_vs_referenced.summary?.matched_count || 0}</div>
                        <div className="text-xs text-green-600">Matched</div>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <div className="text-xl font-bold text-amber-700">{ecComparison.ec_vs_referenced.summary?.unmatched_count || 0}</div>
                        <div className="text-xs text-amber-600">Unmatched</div>
                      </div>
                    </div>
                    
                    {/* Matched */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-green-700 mb-2">✅ Matched (EC ↔ Referenced)</h5>
                      {ecComparison.ec_vs_referenced.matched?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-green-200 rounded-lg overflow-hidden">
                            <thead className="bg-green-50 text-green-800">
                              <tr>
                                <th className="px-3 py-2 text-left">EC Ref</th>
                                <th className="px-3 py-2 text-left">EC Document</th>
                                <th className="px-3 py-2 text-left">Referenced In</th>
                                <th className="px-3 py-2 text-left">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-green-100">
                              {ecComparison.ec_vs_referenced.matched.map((m, idx) => (
                                <tr key={idx} className="bg-white">
                                  <td className="px-3 py-2 font-medium text-indigo-600">{m.ec_ref}</td>
                                  <td className="px-3 py-2">{m.ec_document}</td>
                                  <td className="px-3 py-2 text-purple-600">{m.referenced_in}</td>
                                  <td className="px-3 py-2 text-xs text-green-600">{m.match_status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic p-2 bg-slate-50 rounded">None</p>
                      )}
                    </div>
                    
                    {/* Referenced Not in EC */}
                    {ecComparison.ec_vs_referenced.referenced_not_in_ec?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-red-700 mb-2">🔴 Referenced but NOT in EC</h5>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-red-200 rounded-lg overflow-hidden">
                            <thead className="bg-red-50 text-red-800">
                              <tr>
                                <th className="px-3 py-2 text-left">Referenced In</th>
                                <th className="px-3 py-2 text-left">Referenced Doc Type</th>
                                <th className="px-3 py-2 text-left">EC Match Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-red-100">
                              {ecComparison.ec_vs_referenced.referenced_not_in_ec.map((r, idx) => (
                                <tr key={idx} className="bg-white">
                                  <td className="px-3 py-2 text-purple-600">{r.referenced_in}</td>
                                  <td className="px-3 py-2">{r.referenced_doc_type}</td>
                                  <td className="px-3 py-2 text-xs text-red-600">{r.ec_match_status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* ======================================== */}
                {/* SECTION 4: Inference Summary             */}
                {/* ======================================== */}
                {!ecComparison.no_ec_uploaded && ecComparison.inference && (
                  <div className="bg-slate-100 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-slate-700 mb-2">
                      4️⃣ Inference Summary (Final Consolidation)
                    </h4>
                    <p className="text-xs text-slate-500 mb-3 italic">
                      Final determination after combining upload and reference comparisons
                    </p>
                    
                    {/* Inference Counts */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-slate-700">{ecComparison.inference.total_ec_docs || 0}</div>
                        <div className="text-xs text-slate-600">Total EC Documents</div>
                      </div>
                      <div className="text-center p-3 bg-green-100 rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-green-700">{ecComparison.inference.total_matched_count || 0}</div>
                        <div className="text-xs text-green-600 font-medium">Total Matched</div>
                      </div>
                      <div className="text-center p-3 bg-red-100 rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-red-700">{ecComparison.inference.total_unmatched_count || 0}</div>
                        <div className="text-xs text-red-600 font-medium">Total Unmatched</div>
                      </div>
                    </div>
                    
                    {/* Inference Rule */}
                    <div className="mb-4 p-2 bg-blue-50 rounded border border-blue-200 text-xs text-blue-700">
                      <strong>🔑 Inference Rule:</strong> An EC document is considered MATCHED if it matches an uploaded document OR a referenced document. Otherwise → UNMATCHED
                    </div>
                    
                    {/* Matched List */}
                    {ecComparison.inference.matched?.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-green-700 mb-2">📋 Inferred Matched Documents</h5>
                        <div className="bg-white rounded-lg border border-green-200 p-2">
                          {ecComparison.inference.matched.map((m, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1 border-b border-green-50 last:border-0">
                              <span className="font-medium text-indigo-600">{m.ec_ref}</span>
                              <span className="text-sm">{m.document}</span>
                              <span className="text-xs text-green-600">via {m.matched_via}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Unmatched List */}
                    {ecComparison.inference.unmatched?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-red-700 mb-2">📋 Inferred Unmatched Documents</h5>
                        <div className="bg-white rounded-lg border border-red-200 p-2">
                          {ecComparison.inference.unmatched.map((u, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1 border-b border-red-50 last:border-0">
                              <span className="font-medium text-indigo-600">{u.ec_ref}</span>
                              <span className="text-sm text-red-700">{u.document}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* All Matched Success */}
                    {ecComparison.inference.total_unmatched_count === 0 && ecComparison.inference.total_matched_count > 0 && (
                      <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg text-center">
                        <span className="text-green-800 font-medium">✅ All EC documents have been successfully matched!</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-md font-semibold mb-4">Actions</h3>
              
              <div className="flex flex-wrap gap-4 items-center">
                {/* Download buttons - TXT, PDF, and Word */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => downloadChecklist('txt')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2"
                    data-testid="download-checklist-txt-btn"
                  >
                    <Download className="w-4 h-4" />
                    <span>TXT</span>
                  </button>
                  <button
                    onClick={() => downloadChecklist('pdf')}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center space-x-2"
                    data-testid="download-checklist-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    <span>PDF</span>
                  </button>
                  <button
                    onClick={() => downloadChecklist('docx')}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center space-x-2"
                    data-testid="download-checklist-docx-btn"
                  >
                    <Download className="w-4 h-4" />
                    <span>Word</span>
                  </button>
                </div>
                
                {result.is_ready_for_opinion && (
                  <div className="flex items-center space-x-3 flex-wrap gap-2">
                    <input
                      type="text"
                      placeholder="Case name (optional)"
                      value={caseName}
                      onChange={(e) => setCaseName(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm w-48"
                      data-testid="case-name-input"
                    />
                    <button
                      onClick={saveCase}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300 flex items-center space-x-2"
                      data-testid="save-case-btn"
                    >
                      <Save className="w-4 h-4" />
                      <span>{saving ? 'Saving...' : 'Mark as Ready for Opinion'}</span>
                    </button>
                  </div>
                )}
                
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 flex items-center space-x-2"
                  data-testid="reset-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Start Over</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
